/*
 * How sharp the live camera's picture is: the focus meter of the Spore
 * analysis workbench's microscope camera (components/spores/MicroscopeCamera.js).
 * Pure functions on typed arrays, no DOM, so Vitest runs them on synthetic
 * pictures (focus.test.js).
 *
 *   sharpness()   the mean squared Laplacian of the grey levels over the
 *                 middle of the picture (MIDDLE of it each way), after 2 x 2
 *                 binning, so the camera's grain counts for less than a
 *                 spore's edge, and without the dark ring round an eyepiece's
 *                 view (eyepieceRing: its edge is the field stop, sharp
 *                 whatever the focus). Larger is sharper. The number means nothing on
 *                 its own -- it depends on the slide, the light and the camera
 *                 -- so the meter compares it with the best seen.
 *   focusStep()   the meter: the reading smoothed over the last few
 *                 (SMOOTHING), the best seen (`peak`), which fades by
 *                 PEAK_DECAY a reading so a new field of view can set a new
 *                 best, and where the reading stands against it: 'sharp' (at
 *                 least SHARP of the best), 'close' (CLOSE), else 'blurry';
 *                 'waiting' for the first WARMUP readings or a blank picture.
 */

export const MIDDLE = 0.6;
export const SMOOTHING = 0.5;
export const PEAK_DECAY = 0.997;
export const SHARP = 0.9;
export const CLOSE = 0.7;
export const WARMUP = 3;

/** Rec. 601 luma of an RGBA picture, 0-255. */
export function grayFromRgba(rgba, w, h) {
  const gray = new Float32Array(w * h);
  for (let i = 0; i < gray.length; i += 1) {
    gray[i] = 0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2];
  }
  return gray;
}

/**
 * The dark surround of an eyepiece's view: pixels under RING_DARK of the
 * picture's bright level (its 95th percentile) that are connected to the
 * picture's edge, grown by RING_MARGIN pixels. Its edge is the eyepiece's field
 * stop, sharp whatever the microscope's focus, so it must not count. A dark
 * spore inside the view is not connected to the edge and stays in.
 */
export const RING_DARK = 0.25;
export const RING_MARGIN = 2;

function brightLevel(values) {
  const hist = new Uint32Array(256);
  for (let i = 0; i < values.length; i += 1) hist[Math.max(0, Math.min(255, Math.round(values[i])))] += 1;
  let seen = 0;
  for (let v = 255; v >= 0; v -= 1) {
    seen += hist[v];
    if (seen >= values.length * 0.05) return v;
  }
  return 0;
}

export function eyepieceRing(binned, bw, bh) {
  const dark = RING_DARK * brightLevel(binned);
  const ring = new Uint8Array(bw * bh);
  const stack = [];
  const push = (i) => {
    if (!ring[i] && binned[i] < dark) {
      ring[i] = 1;
      stack.push(i);
    }
  };
  for (let x = 0; x < bw; x += 1) { push(x); push((bh - 1) * bw + x); }
  for (let y = 0; y < bh; y += 1) { push(y * bw); push(y * bw + bw - 1); }
  while (stack.length) {
    const i = stack.pop();
    const x = i % bw;
    if (x > 0) push(i - 1);
    if (x < bw - 1) push(i + 1);
    if (i >= bw) push(i - bw);
    if (i < (bh - 1) * bw) push(i + bw);
  }
  // Grown by the margin, so the field stop's own edge is out too.
  let grown = ring;
  for (let step = 0; step < RING_MARGIN; step += 1) {
    const next = grown.slice();
    for (let i = 0; i < next.length; i += 1) {
      if (grown[i]) continue;
      const x = i % bw;
      if ((x > 0 && grown[i - 1]) || (x < bw - 1 && grown[i + 1]) || (i >= bw && grown[i - bw])
          || (i < (bh - 1) * bw && grown[i + bw])) next[i] = 1;
    }
    grown = next;
  }
  return grown;
}

/** The mean squared Laplacian over the middle `middle` of the picture, binned 2 x 2 first, the eyepiece's ring left out. */
export function sharpness(gray, w, h, { middle = MIDDLE } = {}) {
  const bw = Math.floor(w / 2);
  const bh = Math.floor(h / 2);
  if (bw < 3 || bh < 3) return 0;
  const binned = new Float32Array(bw * bh);
  for (let y = 0; y < bh; y += 1) {
    for (let x = 0; x < bw; x += 1) {
      const i = 2 * y * w + 2 * x;
      binned[y * bw + x] = (gray[i] + gray[i + 1] + gray[i + w] + gray[i + w + 1]) / 4;
    }
  }
  const ring = eyepieceRing(binned, bw, bh);
  const x0 = Math.max(1, Math.floor((bw * (1 - middle)) / 2));
  const x1 = Math.min(bw - 1, Math.ceil((bw * (1 + middle)) / 2));
  const y0 = Math.max(1, Math.floor((bh * (1 - middle)) / 2));
  const y1 = Math.min(bh - 1, Math.ceil((bh * (1 + middle)) / 2));
  let sum = 0;
  let n = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const i = y * bw + x;
      if (ring[i]) continue;
      const lap = binned[i - 1] + binned[i + 1] + binned[i - bw] + binned[i + bw] - 4 * binned[i];
      sum += lap * lap;
      n += 1;
    }
  }
  return n ? sum / n : 0;
}

export const FRESH_FOCUS = Object.freeze({ value: null, peak: 0, readings: 0, ratio: null, level: 'waiting' });

/** The meter after one more reading: {value, peak, readings, ratio (0-1), level}. */
export function focusStep(state, reading) {
  const prev = state || FRESH_FOCUS;
  if (!Number.isFinite(reading) || reading < 0) return prev;
  const value = prev.value === null ? reading : SMOOTHING * reading + (1 - SMOOTHING) * prev.value;
  const peak = Math.max(value, prev.peak * PEAK_DECAY);
  const readings = prev.readings + 1;
  if (readings < WARMUP || peak <= 1e-6) return { value, peak, readings, ratio: null, level: 'waiting' };
  const ratio = Math.min(1, value / peak);
  const level = ratio >= SHARP ? 'sharp' : ratio >= CLOSE ? 'close' : 'blurry';
  return { value, peak, readings, ratio, level };
}
